import { useMemo } from 'react'
import { Field } from 'formular'
import { required, zipCode, canadaPostalCode, universalPostalCode, nonMilitaryZipCode } from 'helpers/validators'
import { useRecurly } from 'modules/payment'


// creates a form for credit card payment
const useCreditCardForm = () => {
  const recurly = useRecurly()

  return useMemo<PaymentModule.CreditCardForm>(() => {
    const firstName = new Field<string>({ validate: [ required ] })
    const lastName = new Field<string>({ validate: [ required ] })
    const postalCode = new Field<string>({ validate: [ required, nonMilitaryZipCode, universalPostalCode ] })
    const country = new Field<string>({ validate: [ required ], value: 'US' })

    // recurly
    const cardNumber = recurly.elements.CardNumberElement({ format: true })
    const cardMonth = recurly.elements.CardMonthElement({ inputType: 'mobileSelect' })
    const cardYear = recurly.elements.CardYearElement({ inputType: 'mobileSelect' })
    const cardCvv = recurly.elements.CardCvvElement()

    const fields = {
      firstName,
      lastName,
      postalCode,
      country,
      cardNumber,
      cardMonth,
      cardYear,
      cardCvv,
    }

    // change country based on postal code
    const countryValidator = {
      'US': zipCode,
      'CA': canadaPostalCode,
    }

    // now we check country by validator rule
    postalCode.on('change', (value) => {
      const possibleCountry = Object.keys(countryValidator).find((country) => {
        const validator = countryValidator[country]

        return !validator(value)
      })

      if (possibleCountry && possibleCountry !== country.state.value) {
        country.set(possibleCountry)
      }
    })

    // this helper sends validate event through recurly element event emitter to show the invalid state
    const setElementError = (element, error) => {
      element.emit('error', error)
    }

    const validateElement = (element) => {
      const error = element.state.valid ? undefined : 'INVALID'
      element.emit('error', error)
      return error
    }

    // emulate form submission and run validation
    const submit = async () => {
      const errors = {}

      // validate each field
      await Promise.all(Object.keys(fields).map(async (fieldName) => {
        const field = fields[fieldName]
        // @ts-ignore
        const error = field instanceof Field ? await field.validate() : validateElement(field)

        if (error) {
          errors[fieldName] = error
        }
      }))

      return {
        errors: Object.keys(errors).length > 0 ? errors : null,
      }
    }

    // display errors from recurly
    const setErrors = (errors) => {
      Object.keys(errors).forEach((fieldName) => {
        const field = fields[fieldName]

        if (!field) {
          return
        }

        if (field instanceof Field) {
          field.setError(errors[fieldName])
        }
        else {
          setElementError(field, errors[fieldName])
        }
      })
    }

    return {
      fields,
      setErrors,
      submit,
    }
  }, [ recurly ])
}


export default useCreditCardForm
